import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import { operators } from "../../../../../../shared/constants/operators.const";
import BaselineMinusIcon from "../../../../../../shared/icons/BaselineMinusIcon";
import {
  ChoiceTaskCondition,
  ChoiceTaskConditions,
  WorkflowEntity,
} from "../../../../../../shared/schemas/workflows";
import { formatOperatorToHuman, InputPath } from "../../../../utils/workflow-node.utils";
import Sidebar from "../../../Sidebar";
import BindButton from "../../Node/components/BindButton";

interface Props {
  conditions: ChoiceTaskConditions;
  availablePaths: InputPath[];
  entityOptions: WorkflowEntity[];
  onUpdateConditions: (conditions: ChoiceTaskConditions) => void;
}

function ChoiceTaskConditionsBuilder(props: Props) {
  const handleAddCondition = () => {
    const emptyCondition: ChoiceTaskCondition = {
      left: {
        type: "path",
        path: null,
      },
      operator: "=",
      right: null,
    };

    props.onUpdateConditions({
      ...props.conditions,
      conditions: [...props.conditions.conditions, emptyCondition],
    });
  };

  const handleRemoveCondition = (index: number) => {
    props.onUpdateConditions({
      ...props.conditions,
      conditions: props.conditions.conditions.filter(
        (_, conditionIndex) => conditionIndex !== index,
      ),
    });
  };

  const handleUpdateCondition = (index: number, condition: ChoiceTaskCondition) => {
    props.onUpdateConditions({
      ...props.conditions,
      conditions: props.conditions.conditions.map((c, conditionIndex) =>
        conditionIndex === index ? condition : c,
      ),
    });
  };

  const handleAddGroup = () => {
    const emptyGroup: ChoiceTaskConditions = {
      operator: "and",
      conditions: [],
    };

    props.onUpdateConditions({
      ...props.conditions,
      conditions: [...props.conditions.conditions, emptyGroup],
    });
  };

  const handleUpdateConditions = (index: number, conditions: ChoiceTaskConditions) => {
    props.onUpdateConditions({
      ...props.conditions,
      conditions: props.conditions.conditions.map((c, conditionIndex) =>
        conditionIndex === index ? conditions : c,
      ),
    });
  };

  return (
    <Flex
      border="1px"
      borderColor="gray.200"
      borderRadius="md"
      direction="column"
      marginStart="2"
      marginTop="2"
      padding="2"
    >
      <Flex gap="2" justifyContent="space-between">
        <GroupTypeRadio
          groupType={props.conditions.operator}
          onChange={(groupType) =>
            props.onUpdateConditions({
              ...props.conditions,
              operator: groupType,
            })
          }
        />
        <Flex gap="1">
          <Button size="xs" onClick={handleAddCondition}>
            + Rule
          </Button>
          <Button size="xs" onClick={handleAddGroup}>
            + Group
          </Button>
        </Flex>
      </Flex>
      {props.conditions.conditions.map((condition, index) => (
        <Flex key={index} justifyContent="space-between" marginTop="1">
          {"conditions" in condition ? (
            <ChoiceTaskConditionsBuilder
              availablePaths={props.availablePaths}
              conditions={condition}
              entityOptions={props.entityOptions}
              onUpdateConditions={(conditions) => handleUpdateConditions(index, conditions)}
            />
          ) : (
            <ChoiceTaskConditionBuilder
              availablePaths={props.availablePaths}
              condition={condition}
              entityOptions={props.entityOptions}
              onUpdate={(condition) => handleUpdateCondition(index, condition)}
            />
          )}
          <Sidebar.SquareButton
            aria-label="remove"
            size="xs"
            onClick={() => handleRemoveCondition(index)}
          >
            <BaselineMinusIcon />
          </Sidebar.SquareButton>
        </Flex>
      ))}
    </Flex>
  );
}

function ChoiceTaskConditionBuilder(props: {
  condition: ChoiceTaskCondition;
  availablePaths: InputPath[];
  entityOptions: WorkflowEntity[];
  onUpdate: (condition: ChoiceTaskCondition) => void;
}) {
  return (
    <Flex gap="1">
      <BindButton
        availablePaths={props.availablePaths}
        availableQueries={null}
        bind={props.condition.left}
        entityOptions={props.entityOptions}
        input={null}
        name="left"
        size="xs"
        onUpdate={(_, bind) => {
          if (bind === null) {
            return props.onUpdate({
              ...props.condition,
              left: {
                type: "path",

                path: null,
              },
            });
          }

          if (bind.type !== "path") {
            return;
          }

          props.onUpdate({
            ...props.condition,
            left: bind,
          });
        }}
      />
      <OperatorSelect
        operator={props.condition.operator}
        onChange={(operator) =>
          props.onUpdate({
            ...props.condition,
            operator,
          })
        }
      />
      {props.condition.operator !== "is empty" &&
        props.condition.operator !== "is not empty" &&
        props.condition.left.path?.output !== undefined && (
          <BindButton
            availablePaths={props.availablePaths}
            availableQueries={null}
            bind={props.condition.right}
            entityOptions={props.entityOptions}
            input={props.condition.left.path.output}
            name="Operand"
            size="xs"
            onUpdate={(_, bind) => {
              if (bind === null) {
                return props.onUpdate({
                  ...props.condition,
                  right: null,
                });
              }

              props.onUpdate({
                ...props.condition,
                right: bind,
              });
            }}
          />
        )}
    </Flex>
  );
}

function OperatorSelect(props: {
  operator: ChoiceTaskCondition["operator"];
  onChange: (operator: ChoiceTaskCondition["operator"]) => void;
}) {
  return (
    <Menu isLazy>
      <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="xs">
        {props.operator}
      </MenuButton>
      <MenuList>
        {operators.map((operator) => (
          <MenuItem key={operator} onClick={() => props.onChange(operator)}>
            {formatOperatorToHuman(operator)}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

function GroupTypeRadio(props: {
  groupType: ChoiceTaskConditions["operator"];
  onChange: (groupType: ChoiceTaskConditions["operator"]) => void;
}) {
  const handleChange = (value: string) => {
    props.onChange(value === "and" ? "and" : "or");
  };

  return (
    <RadioGroup size="sm" value={props.groupType} onChange={handleChange}>
      <Stack direction="row">
        <Radio value="and">And</Radio>
        <Radio value="or">Or</Radio>
      </Stack>
    </RadioGroup>
  );
}

export default ChoiceTaskConditionsBuilder;
